import { ViewDocumentClipboardInputEvent, Plugin } from "ckeditor5";
import { render } from "dom-serializer";
import {
  Document,
  Element,
  isTag,
  isText,
  Node,
  NodeWithChildren,
} from "domhandler";
import { appendChild, prevElementSibling, removeElement } from "domutils";
import { parseDocument } from "htmlparser2";

export default class FixMalformedListsOnPaste extends Plugin {
  static processDocument(doc: Document) {
    let hasChanges = false;

    walkNode(doc, (node) => {
      if (!isTag(node)) return;
      if (node.parent == null) return;

      if (isListTag(node)) {
        hasChanges = moveListIfNecessary(node) || hasChanges;
      }
    });

    return hasChanges;
  }

  init() {
    this.editor.editing.view.document.on<ViewDocumentClipboardInputEvent>(
      "clipboardInput",
      (e, data) => {
        const html = data.dataTransfer.getData("text/html");
        if (html == null) return;

        const dom = parseDocument(html);

        const hasChanges = FixMalformedListsOnPaste.processDocument(dom);

        if (hasChanges) {
          data.dataTransfer.setData("text/html", render(dom));
          data.dataTransfer.setData(
            "application/fixed-malformed-lists+rce5",
            "1",
          );
        }
      },
      { priority: "high" },
    );
  }
}

const walkNode = (node: NodeWithChildren, callback: (node: Node) => void) => {
  callback(node);

  for (const child of [...node.children]) {
    if (isTag(child)) {
      walkNode(child, callback);
    }
  }
};

const moveListIfNecessary = (listElement: Element) => {
  if (listElement.parent == null) return false;
  if (!isListTag(listElement.parent)) return false;

  let target: Element | null = prevElementSibling(listElement);
  if (!isListItemTag(target)) {
    target = prevElementSibling(listElement.parent);
    if (isListTag(target)) {
      target = lastElementChild(target);
    }
  }

  if (!isListItemTag(target)) {
    //
    // We have an improperly nested list, but we don't know where to put it
    //
    return false;
  }

  const previousParent = listElement.parent;

  appendChild(target, listElement);

  if (isEffectivelyEmpty(previousParent)) {
    removeElement(previousParent);
  }

  return true;
};

const isListTag = (e: Node | null | undefined): e is Element =>
  e != null && isTag(e) && (e.name === "ol" || e.name === "ul");

const isListItemTag = (e: Node | null | undefined): e is Element =>
  e != null && isTag(e) && e.name === "li";

const lastElementChild = (e: NodeWithChildren): Element | null => {
  for (let i = e.children.length - 1; i >= 0; i--) {
    const child = e.children[i];
    if (isTag(child)) {
      return child;
    }
  }

  return null;
};

const isEffectivelyEmpty = (e: NodeWithChildren) => {
  if (e.children.length === null) return true;
  return e.children.every((c) => isText(c) && c.data.trim() === "");
};
